import { useState, useEffect } from 'react';
import { fetchConsultations, updateConsultation, createConsultation } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

function InterestedPropertyModal({ isOpen, onClose, property, onSuccess }) {
  const { role } = useAuth();
  const [customerName, setCustomerName] = useState('');
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 최근 상담 고객 목록 로드
  useEffect(() => {
    async function loadRecentCustomers() {
      if (!isOpen) return;
      
      try {
        setIsLoading(true);
        const consultations = await fetchConsultations(role); // 역할 정보 전달
        
        // 고객별로 가장 최근 상담 날짜 추출
        const customerMap = new Map();
        consultations.forEach(consultation => {
          if (!consultation.consultations || consultation.consultations.length === 0) return;
          
          const latestDate = consultation.consultations
            .map(c => new Date(c.date))
            .sort((a, b) => b - a)[0];
          
          const customerName = consultation.customerName || '';
          if (customerName) {
            const existing = customerMap.get(customerName);
            if (!existing || latestDate > existing.latestDate) {
              customerMap.set(customerName, {
                customerName,
                consultationId: consultation.id,
                latestDate,
                consultation
              });
            }
          }
        });
        
        // 최신순으로 정렬
        const sorted = Array.from(customerMap.values())
          .sort((a, b) => b.latestDate - a.latestDate)
          .slice(0, 10); // 최대 10명
        
        setRecentCustomers(sorted);
      } catch (error) {
        console.error('최근 고객 목록 로드 실패:', error);
        setRecentCustomers([]);
      } finally {
        setIsLoading(false);
      }
    }
    
    loadRecentCustomers();
  }, [isOpen, role]);

  // 관심 매물 추가
  const handleAddInterestedProperty = async (selectedCustomerName) => {
    if (!property || !selectedCustomerName?.trim()) return;
    
    const customerNameToUse = selectedCustomerName.trim();
    const propertyName = property.상호 || property.name || '';
    const today = new Date().toISOString().split('T')[0];
    
    setIsSubmitting(true);
    
    try {
      // 해당 고객의 상담 기록 찾기
      const consultations = await fetchConsultations();
      const existingConsultation = consultations.find(
        c => (c.customerName || '').trim() === customerNameToUse
      );
      
      if (existingConsultation) {
        // 기존 상담 기록이 있는 경우
        const consultationData = existingConsultation.consultations || [];
        
        // 오늘 날짜의 상담 기록 찾기
        const todayConsultation = consultationData.find(c => c.date === today);
        
        if (todayConsultation) {
          // 오늘 날짜 상담이 있으면 관심 매물에 추가
          const interestedProperties = Array.isArray(todayConsultation.interestedProperties)
            ? todayConsultation.interestedProperties
            : (todayConsultation.interestedProperties
                ? todayConsultation.interestedProperties.split(',').map(p => p.trim()).filter(Boolean)
                : []);
          
          if (!interestedProperties.includes(propertyName)) {
            interestedProperties.push(propertyName);
          }
          
          // 오늘 상담 기록 업데이트
          const updatedConsultations = consultationData.map(c => 
            c.date === today
              ? { ...c, interestedProperties }
              : c
          );
          
          await updateConsultation(existingConsultation.id, {
            customerName: existingConsultation.customerName,
            consultations: updatedConsultations
          });
        } else {
          // 오늘 날짜 상담이 없으면 새로 추가
          const newConsultation = {
            date: today,
            conditions: {},
            interestedProperties: [propertyName],
            memo: ''
          };
          
          await updateConsultation(existingConsultation.id, {
            customerName: existingConsultation.customerName,
            consultations: [...consultationData, newConsultation]
          });
        }
      } else {
        // 새로운 고객인 경우 새 상담 기록 생성
        await createConsultation({
          customerName: customerNameToUse,
          consultations: [{
            date: today,
            conditions: {},
            interestedProperties: [propertyName],
            memo: ''
          }]
        }, role); // 역할 정보 전달
      }
      
      onSuccess?.();
      onClose();
      setCustomerName('');
    } catch (error) {
      console.error('관심 매물 추가 실패:', error);
      alert('관심 매물 추가에 실패했습니다: ' + (error.message || '알 수 없는 오류'));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      console.log('InterestedPropertyModal 열림', property);
    }
  }, [isOpen, property]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 md:p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        {/* 헤더 */}
        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg md:text-xl font-semibold text-gray-800">
            관심 매물 등록
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* 본문 */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              매물: <span className="font-semibold text-gray-800">{property?.상호 || property?.name || ''}</span>
            </p>
          </div>

          {/* 고객 이름 직접 입력 */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              고객 이름
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && customerName.trim()) {
                    e.preventDefault();
                    handleAddInterestedProperty(customerName);
                  }
                }}
                placeholder="고객 이름을 입력하세요"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                autoFocus
              />
              <button
                type="button"
                onClick={() => handleAddInterestedProperty(customerName)}
                disabled={!customerName.trim() || isSubmitting}
                className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                추가
              </button>
            </div>
          </div>

          {/* 최근 상담 고객 목록 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              최근 상담 고객
            </label>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
              </div>
            ) : recentCustomers.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {recentCustomers.map((customer, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleAddInterestedProperty(customer.customerName)}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 text-left bg-gray-50 hover:bg-orange-50 border border-gray-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="text-sm font-medium text-gray-800">
                      {customer.customerName.endsWith('님') ? customer.customerName : `${customer.customerName}님`}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      최근 상담: {customer.latestDate.toLocaleDateString('ko-KR')}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 text-center py-8">
                최근 상담 고객이 없습니다.
              </div>
            )}
          </div>
        </div>

        {/* 푸터 */}
        <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 flex items-center justify-end flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            취소
          </button>
        </div>
      </div>
    </div>
  );
}

export default InterestedPropertyModal;

